import React from 'react';
import {
  ActivityIndicator,
  Pressable,
  Text,
  View,
} from 'react-native';
import type { TextStyle, ViewStyle } from 'react-native';

import { tokens } from '@design';
import { FONTS } from '../../../constants/config';
import { useLanguage } from '../../../contexts/LanguageContext';
import { getFlexRow } from '../../../utils/rtl';

import type {
  ButtonProps,
  ButtonSize,
  ButtonVariant,
} from './Button.types';

interface ButtonStyleResult {
  container: ViewStyle;
  containerDisabled: ViewStyle;
  text: TextStyle;
  textDisabled: TextStyle;
  spinnerColor: string;
}

interface SizeTokens {
  minHeight: number;
  paddingHorizontal: number;
  paddingVertical: number;
  fontSize: number;
  iconGap: number;
}

const SIZE_TOKENS: Readonly<Record<ButtonSize, SizeTokens>> = {
  md: {
    minHeight: tokens.spacing.absolute["11"],
    paddingHorizontal: tokens.spacing.absolute["6"],
    paddingVertical: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    iconGap: tokens.spacing.absolute["2"],
  },
  sm: {
    minHeight: tokens.spacing.absolute["9"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["2"],
    fontSize: tokens.typography.paragraph["paragraph extra-small font size"],
    iconGap: tokens.spacing.absolute["1_5"],
  },
};

const styleCache = new Map<string, ButtonStyleResult>();

function getButtonStyles(
  variant: ButtonVariant,
  size: ButtonSize,
): ButtonStyleResult {
  const cacheKey = `${variant}:${size}`;
  const cached = styleCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const sizeTokens = SIZE_TOKENS[size];

  const container: ViewStyle = {
    minHeight: sizeTokens.minHeight,
    paddingHorizontal: sizeTokens.paddingHorizontal,
    paddingVertical: sizeTokens.paddingVertical,
    borderRadius: tokens.radii.absolute["radius-8"],
    alignItems: 'center',
    justifyContent: 'center',
  };

  const text: TextStyle = {
    fontFamily: FONTS.medium,
    fontSize: sizeTokens.fontSize,
    textAlign: 'center',
  };

  let result: ButtonStyleResult;

  switch (variant) {
    case 'primary':
      result = {
        container: {
          ...container,
          backgroundColor: tokens.colors.semantic.general["Main Color"],
        },
        containerDisabled: {
          backgroundColor: tokens.colors.raw.gray["300"],
        },
        text: {
          ...text,
          color: tokens.colors.raw.white,
        },
        textDisabled: {},
        spinnerColor: tokens.colors.raw.white,
      };
      break;

    case 'secondary':
      result = {
        container: {
          ...container,
          backgroundColor: tokens.colors.raw.white,
          borderWidth: tokens.spacing.absolute["px"],
          borderColor: tokens.colors.raw.gray["200"],
        },
        containerDisabled: {
          backgroundColor: tokens.colors.raw.gray["100"],
          borderColor: tokens.colors.raw.gray["200"],
        },
        text: {
          ...text,
          color: tokens.colors.semantic.general.foreground,
        },
        textDisabled: {
          color: tokens.colors.raw.gray["400"],
        },
        spinnerColor: tokens.colors.semantic.general.foreground,
      };
      break;

    case 'destructive':
      result = {
        container: {
          ...container,
          backgroundColor: tokens.colors.raw.red["500"],
        },
        containerDisabled: {
          backgroundColor: tokens.colors.raw.gray["300"],
        },
        text: {
          ...text,
          color: tokens.colors.raw.white,
        },
        textDisabled: {},
        spinnerColor: tokens.colors.raw.white,
      };
      break;

    case 'ghost':
      result = {
        container: {
          ...container,
          backgroundColor: 'transparent',
        },
        containerDisabled: {},
        text: {
          ...text,
          color: tokens.colors.semantic.general.foreground,
        },
        textDisabled: {
          color: tokens.colors.raw.gray["400"],
        },
        spinnerColor: tokens.colors.semantic.general.foreground,
      };
      break;
  }

  styleCache.set(cacheKey, result);
  return result;
}

export function Button(props: ButtonProps) {
  const {
    variant = 'primary',
    size = 'md',
    children,
    onPress,
    leadingIcon,
    trailingIcon,
    fullWidth = false,
    disabled = false,
    loading = false,
    accessibilityLabel,
    style,
    testID,
  } = props;

  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const styles = getButtonStyles(variant, size);
  const isInactive = disabled || loading;
  const sizeTokens = SIZE_TOKENS[size];

  const resolvedAccessibilityLabel =
    accessibilityLabel ?? (typeof children === 'string' ? children : undefined);

  return (
    <Pressable
      onPress={onPress}
      disabled={isInactive}
      accessibilityRole="button"
      accessibilityLabel={resolvedAccessibilityLabel}
      accessibilityState={{ disabled: isInactive, busy: loading }}
      testID={testID}
      style={({ pressed }) => [
        styles.container,
        isInactive && styles.containerDisabled,
        fullWidth && { width: '100%' },
        pressed && !isInactive && { opacity: 0.85 },
        style,
      ]}
    >
      <View
        style={{
          flexDirection: getFlexRow(isRTL),
          alignItems: 'center',
          justifyContent: 'center',
          gap: sizeTokens.iconGap,
        }}
      >
        {loading ? (
          <ActivityIndicator size="small" color={styles.spinnerColor} />
        ) : (
          leadingIcon
        )}
        <Text
          style={[styles.text, isInactive && styles.textDisabled]}
          numberOfLines={1}
        >
          {children}
        </Text>
        {trailingIcon}
      </View>
    </Pressable>
  );
}
